// Versioned JSON saves. localStorage by default, IndexedDB when the save exceeds 2 MB.
// serialize/deserialize are pure so they can be tested headless and used for export/import.

import { TUNABLES } from "@degen/shared/config/tunables";
import { SAVE_VERSION, type GameState } from "./state";
import { generateAvatar } from "./avatar/traits";
import { createSocial } from "./social/feed";
import { createAirdrops } from "./airdrops/protocols";
import { createNft } from "./nft/drops";
import { createEvents } from "./events/sleuth";
import { createPets } from "./pets/pets";
import { createCars } from "./cars/cars";
import { APARTMENT_RANK, createHouses, grantStarterHome, starterId } from "./houses/houses";
import { createMessages } from "./messages/messages";
import { createGasState } from "./gas";
import { starterPhone } from "./phones/phones";
import { createVano } from "./vano/vano";
import { createHackathons } from "./hackathons/hackathons";
import { createPerps } from "./perps/perps";
import { createProgress } from "./progress/progress";
import { generatePfpTraits } from "./pfp/traits";
import { Rng } from "./rng";

export interface SaveFile {
  version: number;
  seed: number;
  tick: number;
  state: GameState;
}

/** Trim bulky buffers that the sim doesn't need for determinism. */
function trimForSave(state: GameState): GameState {
  const copy: GameState = JSON.parse(JSON.stringify(state));
  const cap = TUNABLES.market.candleCapOnSave;
  for (const id in copy.market.tokens) {
    const t = copy.market.tokens[id];
    if (t.candles.length > cap) t.candles = t.candles.slice(-cap);
  }
  return copy;
}

export function serialize(state: GameState): string {
  const file: SaveFile = { version: SAVE_VERSION, seed: state.seed, tick: state.tick, state: trimForSave(state) };
  return JSON.stringify(file);
}

export function deserialize(json: string): GameState {
  const raw = JSON.parse(json) as SaveFile;
  if (!raw || typeof raw !== "object" || !raw.state) throw new Error("Not a Degen World save.");
  const file = migrate(raw);
  // The SaveFile header fields are mirrors of the state; the state wins.
  return file.state;
}

/** Upgrade older saves step by step. v1 is current, so this is a stub for now. */
export function migrate(file: SaveFile): SaveFile {
  if (file.version > SAVE_VERSION) throw new Error(`Save is from a newer version (${file.version}).`);
  // Example for the future:
  // if (file.version === 1) { file = migrateV1toV2(file); }
  // Pre-release v1 saves made before sprite avatars existed: give them an anon avatar.
  const p = file.state.player;
  if (p && p.pfp && !Object.keys(p.pfp.traits ?? {}).length) p.pfp.traits = generatePfpTraits(p.pfp.seed);
  if (p && !p.avatar) p.avatar = generateAvatar(p.pfp?.seed ?? 0, "anon");
  // Pre-release saves made before Chirp existed.
  if (p && !p.daily.postTypes) p.daily.postTypes = {};
  if (p && !p.launches) p.launches = [];
  if (p) {
    p.stats.collectionsLaunched ??= 0;
    p.stats.rugsPulled ??= 0;
  }
  if (file.state.logSeq === undefined) {
    file.state.logSeq = file.state.log.length;
    file.state.log.forEach((e, i) => (e.seq = i));
  }
  if (p && !p.tutorial) p.tutorial = { track: p.origin, step: 99, dismissed: true }; // existing saves skip the tutorial
  if (p && p.comebacks === undefined) p.comebacks = 0;
  // Saves from before the DGN gas coin: start with a fresh wallet of gas.
  if (p && p.dgn === undefined) p.dgn = TUNABLES.gas.start;
  if (p && !p.gas) p.gas = createGasState();
  if (p && !p.phone) {
    p.phone = starterPhone(p.origin === "kol");
    p.phones = [p.phone];
  }
  if (!file.state.vano) file.state.vano = createVano();
  // Saves from before run recording can't be replayed: kept playable, but off verified leaderboards.
  if (p && !file.state.run) file.state.run = { input: { name: p.name, handle: p.handle, bio: p.bio, pfp: p.pfp, avatar: p.avatar, origin: p.origin }, actions: [], legacy: true };
  if (!file.state.hackathons) file.state.hackathons = createHackathons();
  if (!file.state.perps) file.state.perps = createPerps();
  if (!file.state.progress) file.state.progress = createProgress();
  if (file.state.rekt === undefined) file.state.rekt = null;
  if (file.state.events && !file.state.events.pending) Object.assign(file.state.events, { pending: [], nextId: 1, collabs: [], asleep: false });
  if (!file.state.pets) file.state.pets = createPets();
  if (!file.state.market.presales) file.state.market.presales = [];
  if (!file.state.cars) file.state.cars = createCars();
  if (!file.state.houses) file.state.houses = createHouses();
  // Saves from before starter homes: rent one that matches your status.
  if (p && !starterId(file.state)) grantStarterHome(file.state, p.origin === "kol" || p.rank >= APARTMENT_RANK);
  if (!file.state.messages) file.state.messages = createMessages();
  if (file.state.social && !file.state.social.giveaways) file.state.social.giveaways = [];
  if (!file.state.airdrops) file.state.airdrops = createAirdrops();
  if (!file.state.nft) file.state.nft = createNft();
  if (!file.state.events) file.state.events = createEvents();
  if (!file.state.social) file.state.social = createSocial(Rng.fromSeed(file.state.seed ^ 0x50c1a1));
  if (file.state.social.logSeen === undefined) file.state.social.logSeen = file.state.logSeq;
  return file;
}
